import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  LocalShipping,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { brandTokens } from "../theme";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const success = await login(email, password);
      if (success) {
        navigate("/dashboard");
      } else {
        setError("Invalid email or password");
      }
    } catch (error) {
      setError("An error occurred during login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {/* Left Brand Panel */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          width: "40%",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(160deg, ${brandTokens.navyLight} 0%, ${brandTokens.navyDark} 100%)`,
          p: 6,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* subtle radial glow */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `
              radial-gradient(ellipse at 30% 70%, rgba(245,158,11,0.1) 0%, transparent 60%),
              radial-gradient(ellipse at 70% 20%, rgba(245,158,11,0.06) 0%, transparent 50%)
            `,
          }}
        />
        {/* dot grid */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          {/* Logo */}
          <img
            src={`${API_URL}/logotypes/logo.png`}
            alt="Geva Logistics"
            style={{
              maxWidth: 180,
              maxHeight: 64,
              objectFit: "contain",
              opacity: 0.85,
              marginBottom: 32,
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
            onError={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              if (!img.src.includes("/logo.png") || img.src.includes("logotypes")) {
                img.src = "/logo.png";
              } else {
                img.style.display = "none";
              }
            }}
          />

          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: "14px",
              bgcolor: brandTokens.amber,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
              boxShadow: `0 0 0 10px rgba(245,158,11,0.1)`,
            }}
          >
            <LocalShipping sx={{ color: brandTokens.navy, fontSize: 30 }} />
          </Box>

          <Typography
            variant="h4"
            fontWeight={800}
            sx={{ color: "#fff", mb: 1, letterSpacing: "-0.02em" }}
          >
            Driver Portal
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255,255,255,0.5)",
              lineHeight: 1.7,
              maxWidth: 260,
              mx: "auto",
            }}
          >
            View your assigned shipments, update location, and upload delivery
            photos.
          </Typography>
        </Box>
      </Box>

      {/* Right Form Panel */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#F8FAFC",
          p: { xs: 2, sm: 4 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 380 }}>
          {/* Mobile logo */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              justifyContent: "center",
              mb: 4,
            }}
          >
            <img
              src={`${API_URL}/logotypes/invoice_logo.png`}
              alt="Geva Logistics"
              style={{ maxWidth: 140, maxHeight: 50, objectFit: "contain" }}
              onError={(e) => {
                const img = e.currentTarget as HTMLImageElement;
                if (!img.src.includes("/invoice_logo.png") || img.src.includes("logotypes")) {
                  img.src = "/invoice_logo.png";
                } else {
                  img.style.display = "none";
                }
              }}
            />
          </Box>

          <Typography
            variant="h5"
            fontWeight={800}
            color="text.primary"
            sx={{ mb: 0.5, letterSpacing: "-0.02em" }}
          >
            Driver Sign In
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Access your delivery dashboard
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
          >
            <TextField
              required
              fullWidth
              label="Email Address"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ fontSize: 18, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              required
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ fontSize: 18, color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 18 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 0.5,
                py: 1.4,
                fontSize: "0.95rem",
                background: `linear-gradient(135deg, ${brandTokens.navyLight} 0%, ${brandTokens.navy} 100%)`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${brandTokens.navy} 0%, ${brandTokens.navyDark} 100%)`,
                },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Sign In"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
